import React, { useState, useEffect, useRef } from "react";
import { useStaticQuery, graphql, Link } from "gatsby";
import styled from "styled-components";
import { use100vh } from "react-div-100vh";
import { PrismicRichText } from "@prismicio/react";
import { useLockBodyScroll, useToggle, useScrollbarWidth } from "react-use";

// Components
import { AboutSectionTitle } from "../about/about-section-title";
import NewsFilter from "../news/news-filter";

// Hooks
import { useMediaQuery } from "../hooks/useMediaQuery";

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  z-index: 200;

  & .header-bar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    & .background-bar {
      width: 50vw;
      padding: 0 30px;

      &.left {
        & .mobile-menu-toggle {
          display: none;
        }

        // background-color: #fff;

        & ol {
          display: flex;
          flex-direction: row;
          gap: 30px;

          & li {
            line-height: 70px;

            & a {
              &:hover {
                font-weight: 700;
              }
              &.active {
                font-weight: 700;
              }
            }
          }
        }
      }

      &.right {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
        gap: 30px;

        background-color: ${(props) => props.backgroundColor};
        width: calc(50vw - ${(props) => props.sbw}px);
      }

      /* & button {
        &.menu-toggle {
          margin: 0 0 0 auto;
          display: block;
        }

        @media (hover: hover) and (pointer: fine) {
          &:hover {
            font-family: "Selecta Bold", sans-serif;
          }
        }
      } */

      /* @media (max-width: 1000px) {
        width: auto;
      } */
    }

    & h1,
    & h2,
    & .menu-toggle {
      /* font-size: 24px; */
      font-size: 20px;
      line-height: 70px;

      @media (max-width: 1000px) {
        line-height: 40px;
      }
    }
  }

  @media (max-width: 999px) {
    & .header-bar {
      background-color: ${(props) => props.backgroundColor};
      padding: 0 15px;
      gap: 15px;

      & .background-bar {
        padding: 0;

        width: auto !important;

        gap: 15px !important;

        &.left {
          line-height: 40px;

          & .mobile-menu-toggle {
            display: block;
          }

          & ol {
            display: none;
          }
        }
      }
    }
  }
`;

const Menu = styled.div`
  position: fixed;
  top: 0;
  right: 0;

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

  width: 50vw;
  height: ${(props) => props.height}px;

  transform: translateX(${(props) => props.translateX});
  transition: 500ms transform ease;

  padding: 30px;

  background-color: ${(props) => props.backgroundColor};

  z-index: 210;

  /* font-size: 24px; */
  /* line-height: 28px; */
  font-size: 20px;
  line-height: 125%;

  /* @media (max-width: 1000px) { */
  /* font-size: 20px; */
  /* } */

  & .top-section,
  & .bottom-section {
    flex: 100%;
  }

  & .top-level-navigation {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;

    & button {
      display: none;

      @media (max-width: 999px) {
        display: block;
      }
    }
  }

  & .bottom-section {
    margin: auto 0 0 0;

    & p {
      &:last-of-type {
        margin: 1em 0 0 0;
      }
    }
  }

  & ol {
    margin: 30px 0 0 0;
  }

  & a {
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        font-family: "Selecta Bold", sans-serif;
      }
    }
  }

  @media (max-width: 999px) {
    width: 100vw;
    padding: 15px;

    & ol {
      line-height: 150%;
    }
  }
`;

export const Header = ({ location, pageContext }) => {
  const [isMenuOpen, setIsMenuOpen] = useToggle(false);

  // Media Query
  let isDesktop = useMediaQuery("(min-width: 1000px)");

  useLockBodyScroll(isMenuOpen);

  const height = use100vh();
  const sbw = useScrollbarWidth();
  const ref = useRef(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const data = useStaticQuery(graphql`
    {
      prismicMenu {
        data {
          menu {
            item {
              document {
                ... on PrismicAbout {
                  id
                  url
                  data {
                    title {
                      text
                    }
                  }
                }
                ... on PrismicProjects {
                  id
                  url
                  data {
                    title {
                      text
                    }
                  }
                }
                ... on PrismicNews {
                  id
                  url
                  data {
                    title {
                      text
                    }
                  }
                }
              }
            }
          }
          contact_text {
            richText
          }
        }
      }
    }
  `);

  const menuItems = data.prismicMenu.data.menu
    .filter((item) => item.item.document !== null)
    .map((item, index) => (
      <li key={`single_menu_item_${index}`}>
        <Link
          to={item.item.document.url}
          activeClassName="active"
          partiallyActive
        >
          {item.item.document.data.title.text}
        </Link>
      </li>
    ));

  return (
    <>
      <HeaderWrapper
        backgroundColor={pageContext.pageColor}
        sbw={isMenuOpen ? 0 : sbw}
      >
        <div className="header-bar">
          <div className="background-bar left">
            <ol>{menuItems}</ol>

            <Link to={`/`} className="mobile-menu-toggle">
              {location.pathname === `/` ? `Jamie Whelan Studio` : `JWS`}
            </Link>
          </div>

          <div className="background-bar right">
            {pageContext.pageType !== `about` &&
              pageContext.pageType !== `news` &&
              isDesktop && <h1>{pageContext.pageTitle}</h1>}
            {pageContext.pageType === `news` && <NewsFilter />}
            {pageContext.pageType === `about` && <AboutSectionTitle />}

            {isDesktop ? (
              <div>
                <Link to={`/`} className="menu-toggle">
                  {isDesktop || location === `/`
                    ? `Jamie Whelan Studio`
                    : `JWS`}
                </Link>
              </div>
            ) : (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="menu-toggle"
              >
                Menu
              </button>
            )}
          </div>
        </div>

        <Menu
          backgroundColor={pageContext.pageColor}
          translateX={isMenuOpen ? `0` : `100%`}
          height={height}
          ref={ref}
          onMouseLeave={() => setIsMenuOpen(false)}
        >
          <div className="top-section">
            <div className="top-level-navigation">
              <Link to={`/`} onClick={() => setIsMenuOpen(false)}>
                Jamie Whelan Studio
              </Link>

              <button onClick={() => setIsMenuOpen(false)}>✕</button>
            </div>

            <ol>{menuItems}</ol>
          </div>

          <div className="bottom-section">
            <PrismicRichText
              field={data.prismicMenu.data.contact_text.richText}
            />
          </div>
        </Menu>
      </HeaderWrapper>
    </>
  );
};
